import {
  SvcActionPlanEscalationHistory,
  SvcModalActionPlanByIdResponse,
  SvcModalActionPlanSystemReference,
} from '../interfaces/svc-modal-action-plan.interface';

// Digital Obeya reference type ids.
//
// Creation (notification/create URL — kept untouched on escalate): a Room arrives
// as type 3, a Board as type 8 and a Widget as type 9, each paired by index with
// its referenceValue.
//
// Escalate (Obeya only): the escalate save writes a parallel set — Room as 10,
// Board as 11 and Widget as 12. The backend keeps the creation set (3/8/9) and
// ADDS the escalate set (10/11/12); GET /ActionPlan/{id} returns both, so the
// escalate reference reflects the current destination after an escalate.
export enum ObeyaReferenceType {
  Room = 3,
  Board = 8,
  Widget = 9,
  EscalateRoom = 10,
  EscalateBoard = 11,
  EscalateWidget = 12,
}

// Each creation reference type maps to its escalate counterpart. The escalate
// reference wins over the creation one when resolving the current room/board/widget.
const ESCALATE_BY_CREATION: ReadonlyMap<ObeyaReferenceType, ObeyaReferenceType> = new Map([
  [ObeyaReferenceType.Room, ObeyaReferenceType.EscalateRoom],
  [ObeyaReferenceType.Board, ObeyaReferenceType.EscalateBoard],
  [ObeyaReferenceType.Widget, ObeyaReferenceType.EscalateWidget],
]);

/** Known Digital Obeya application ids: core (revamp) and the legacy host. */
export const OBEYA_APPLICATION_IDS: readonly string[] = [
  'BCE987A3-58DD-4830-B9E4-08A9DE7A0A07',
  'DB6CA947-4EC3-4DF2-B76A-194A4F7EFD31',
];

export function isObeyaApplicationId(applicationId: string | null | undefined): boolean {
  const normalized = (applicationId ?? '').trim().toUpperCase();
  return normalized.length > 0 && OBEYA_APPLICATION_IDS.includes(normalized);
}

export function isObeyaSystemSelected(
  selectedSystemUniqueId: number | string | null | undefined,
  obeyaSystemUniqueIds: readonly number[] | null | undefined,
): boolean {
  const selected = Number(selectedSystemUniqueId);
  if (!Number.isFinite(selected) || selected <= 0) return false;
  return (obeyaSystemUniqueIds ?? []).includes(selected);
}

/**
 * Whether the modal runs in Digital Obeya context. An existing plan is flagged
 * by the backend (`isObeya` on GET /ActionPlan/{id}); a brand-new plan is
 * detected from the originating applicationId (notification/iframe URL).
 */
export function resolveIsObeyaContext(args: {
  plan?: Pick<SvcModalActionPlanByIdResponse, 'isObeya'> | null;
  applicationId?: string | null;
}): boolean {
  return args.plan?.isObeya === true || isObeyaApplicationId(args.applicationId);
}

function toPositiveId(value: number | null | undefined): number | null {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function resolveObeyaPrefillReference(
  plan: SvcModalActionPlanByIdResponse | null | undefined,
  referenceTypeId: ObeyaReferenceType
): number | null {
  const references = plan?.actionPlanSystemReferences ?? [];

  // The escalate reference (10/11/12) holds the current destination after an
  // escalate and wins over the original creation reference (3/8/9). getById
  // returns both sets, so we read them straight from actionPlanSystemReferences.
  const escalateTypeId = ESCALATE_BY_CREATION.get(referenceTypeId);
  if (escalateTypeId != null) {
    const escalateValue = findReferenceValue(references, escalateTypeId);
    if (escalateValue != null) return escalateValue;
  }

  return findReferenceValue(references, referenceTypeId);
}

function findReferenceValue(
  references: readonly SvcModalActionPlanSystemReference[],
  referenceTypeId: ObeyaReferenceType
): number | null {
  const reference = references.find((ref) => ref?.referenceTypeId === referenceTypeId);
  return toPositiveId(reference?.referenceValue);
}

/**
 * Site the plan currently lives in after escalations: the `newSiteId` of the
 * most recent `escalationHistories` entry (by `historyDate`). Used to scope the
 * escalate "Responsável" list to that site (GET /User/UsersAndGuests/names?siteId=)
 * and to preselect the Obeya Site combo. Returns null when the plan was never
 * escalated — the caller then falls back to the global user list / plan site.
 */
export function resolveLatestEscalatedSiteId(
  plan: Pick<SvcModalActionPlanByIdResponse, 'escalationHistories'> | null | undefined
): number | null {
  const histories = plan?.escalationHistories ?? [];
  let latest: SvcActionPlanEscalationHistory | null = null;
  for (const history of histories) {
    if (latest == null || toEpoch(history?.historyDate) > toEpoch(latest.historyDate)) {
      latest = history;
    }
  }
  return toPositiveId(latest?.newSiteId);
}

function toEpoch(value: string | null | undefined): number {
  const parsed = Date.parse(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Old/new site pair of the most recent escalation. Prefers the canonical
 * Room entries (`referenceTypeId` 10) — the backend writes one per escalate —
 * and falls back to whatever entries exist for non-Obeya escalations. Returns
 * null when the plan was never escalated. Consumed by the site-scoped
 * permission gate (`SvcActionPlanPermissionService`).
 */
export function resolveLatestEscalationSites(
  plan: Pick<SvcModalActionPlanByIdResponse, 'escalationHistories'> | null | undefined
): { oldSiteId: number | null; newSiteId: number | null } | null {
  const histories = plan?.escalationHistories ?? [];
  if (!histories.length) return null;

  const roomEntries = histories.filter((history) => history?.referenceTypeId === ObeyaReferenceType.EscalateRoom);
  const candidates = roomEntries.length ? roomEntries : histories;

  let latest: SvcActionPlanEscalationHistory | null = null;
  for (const history of candidates) {
    if (latest == null || toEpoch(history?.historyDate) > toEpoch(latest.historyDate)) {
      latest = history;
    }
  }
  if (!latest) return null;

  return { oldSiteId: toPositiveId(latest.oldSiteId), newSiteId: toPositiveId(latest.newSiteId) };
}

export function buildObeyaSystemReferences(
  roomId: number | null | undefined,
  boardId: number | null | undefined
): SvcModalActionPlanSystemReference[] {
  // Escalate-only builder (Obeya). Emits the escalate reference types Room=10 and
  // Board=11; creation uses 3/8/9 through the notification-params path. The widget
  // (type 12) is intentionally NOT sent on escalate — the backend rejects it.
  return [
    { referenceValue: Number(roomId ?? 0), referenceTypeId: ObeyaReferenceType.EscalateRoom },
    { referenceValue: Number(boardId ?? 0), referenceTypeId: ObeyaReferenceType.EscalateBoard },
  ];
}
